<template>
  <article>
    <activity-stage :customRootContainerClass="'full-height'">
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content" style="height: 100%">
        <assessment-helper></assessment-helper>
        <div class="mat-man-match">
          <div class="drag-completed" v-if="showImage">
            <span class="image-header">{{ currentRound.shape }}</span>
            <img
              :alt="currentRound?.completedImage?.image?.description"
              class="completed-image"
              :src="currentRound?.completedImage?.image?.src"
            />
          </div>
          <div class="drag-panel" v-if="!showImage">
            <div class="drag-container">
              <img
                :alt="currentRound?.emptyImage?.image?.description"
                :aria-label="`Droppable area for ${currentRound.shape}`"
                id="dragging-area"
                :src="currentRound?.emptyImage?.image?.src"
              />
            </div>
            <div class="draggable-items" :class="{ isMobile }">
              <button
                @keyup.enter="handleOnShapeEnterEvent"
                v-for="(answer, index) in currentRound.answers"
                :aria-label="answer.shape"
                :key="index"
                class="draggable-item pure-button"
              >
                <div class="image-container">
                  <img
                    :alt="answer.shape"
                    :id="answer.shape"
                    class="selected-image"
                    :src="answer.selectedImage.image.src"
                  />
                  <img alt class="unselected-image" :src="answer.unselectedImage.image.src" />
                </div>
                <div class="image-text">{{ answer.shape }}</div>
              </button>
            </div>
          </div>
        </div>
        <span aria-atomic="true" id="hidden-mat-man-activity-description" role="alert"
          >Mat Man's torso.</span
        >
      </article>
    </activity-stage>
  </article>
</template>

<script>
import { TweenMax, Power0 } from 'gsap';
import Draggable from 'gsap/Draggable';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import AssetLoader from 'mixins/AssetLoader';
import ActivityManager from 'mixins/ActivityManager';
import ErrorLogger from 'mixins/ErrorLogger';
import { hasTouchedBoundings, getActivityBoundings } from 'utils/activity-util';
import { createNamespacedHelpers } from 'vuex';

import ActivityStage from 'components/activity/ActivityStage.vue';
import AssessmentHelper from 'components/utils/AssessmentHelper.vue';

const application = createNamespacedHelpers('application');

export default {
  components: { ActivityStage, AssessmentHelper },

  mixins: [AssetLoader, ActivityManager, TypingActivityHelper, ErrorLogger],

  data() {
    return {
      showImage: false,
      sounds: null
    };
  },

  computed: {
    ...application.mapState({
      isMobile: state => state.device.isMobile
    })
  },

  methods: {
    async loadActivity() {
      this.createRounds({ availableShapes: this.activityData.availableShapes });
      await this.loadImages();
      this.sounds = await this.loadSounds();
    },

    async loadSounds() {
      const { rounds } = this.activityData;
      const sounds = rounds.map(({ shape, shapeAudio }) => ({
        audio: shapeAudio.audio.src,
        shape
      }));

      this.queueAudio(sounds.map(sound => sound.audio));
      const cache = await this.downloadAudio();
      return sounds.map(sound => ({
        shape: sound.shape,
        howler: cache.get(sound.audio)
      }));
    },

    async loadImages() {
      const { rounds } = this.activityData;

      rounds.forEach(({ answers, emptyImage, completedImage }) => {
        this.queueImage([emptyImage.image.src, completedImage.image.src]);
        answers.forEach(answer =>
          this.queueImage([answer.selectedImage.image.src, answer.unselectedImage.image.src])
        );
      });

      await this.downloadImages();
    },

    startActivity() {
      this.hideKeyboard();
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);

      this.setupNewRound();
      TweenMax.delayedCall(1, () => {
        TweenMax.to('.drag-panel', 2, { opacity: 1 });
        this.handleRoundStart();
      });
    },

    getVerticalDropThreshold(matManImageEl) {
      return (60 / 100) * (matManImageEl.height / 3);
    },

    handleDrop(context) {
      const self = this;
      const {
        target,
        vars: { shape }
      } = context;
      const draggingAreaDOM = document.getElementById('dragging-area');
      const dragTargetCoordinates = target.getBoundingClientRect();
      const draggingDOMRect = draggingAreaDOM.getBoundingClientRect();
      const verticalThreshold = this.getVerticalDropThreshold(draggingAreaDOM);

      const isCorrectDrag =
        draggingDOMRect.top + verticalThreshold < dragTargetCoordinates.top &&
        draggingDOMRect.bottom - verticalThreshold > dragTargetCoordinates.bottom &&
        draggingDOMRect.left < dragTargetCoordinates.left &&
        draggingDOMRect.right > dragTargetCoordinates.right &&
        shape === this.currentRound.shape;

      if (isCorrectDrag) {
        this.sndBell();
        this.logCorrectCharacter();
        this.sounds.forEach(sound => {
          if (sound.shape === this.currentRound.shape) {
            sound.howler.play();
          }
        });

        this.handleOnCorrectShapeSelection();
      } else {
        this.setHiddenMatManActivityDescription('Incorrect selection, try again.');
        setTimeout(() => {
          this.setHiddenMatManActivityDescription("Mat Man's torso.");
        }, 2000);

        this.sndError();
        this.logError(shape, this.currentRound.shape, 'DNDplace');
        TweenMax.to(target, 0.5, { x: '-50%', y: 0, ease: Power0.easeNone });
      }
    },

    handleRoundStart(isNewRound = false) {
      const self = this;

      if (isNewRound) {
        self.createDraggables();
        TweenMax.to('.drag-panel', 1, { opacity: 1 });
      } else {
        // first round
        TweenMax.to('.drag-panel', 0.5, {
          opacity: 1,
          onComplete() {
            self.createDraggables();
          }
        });
      }
    },

    createDraggables() {
      let bounds = null;
      const self = this;
      const { isMobile } = this.userDevice;

      this.currentRound.answers.forEach(answer => {
        let shapeTimer = 0;
        let hasHitBoundary = false;
        Draggable.create(`#${answer.shape}`, {
          autoScroll: 1,
          cursor: 'grab',
          shape: answer.shape,
          onPress() {
            if (bounds) return;
            // get DOMRect bounding
            bounds = getActivityBoundings();
          },
          onDrag() {
            if (self.timerActive) {
              self.resetTimer();
            }
            // work-around for iOS not firing touchend on tablet
            if (!isMobile) return;
            clearTimeout(shapeTimer);
            const { pointerX, pointerY } = this;
            const hasTouched = hasTouchedBoundings(pointerX, pointerY, bounds);
            if (hasTouched && !hasHitBoundary) {
              shapeTimer = setTimeout(() => {
                hasHitBoundary = true;
                self.handleDrop(this);
              }, 200);
            }
          },
          onDragStart() {
            TweenMax.set(`#${answer.shape}`, { cursor: 'grabbing' });
          },
          onDragEnd() {
            clearTimeout(shapeTimer);
            hasHitBoundary = false;
            self.handleDrop(this);
          }
        });
      });
    },

    handleOnShapeEnterEvent(event) {
      const currentRoundShape = this.currentRound.shape.toLowerCase();
      const shapeSelection = event.target.innerText.toLowerCase();

      if (currentRoundShape === shapeSelection) {
        this.sndBell();
        this.logCorrectCharacter();
        this.sounds.forEach(sound => {
          if (sound.shape === this.currentRound.shape) {
            sound.howler.play();
          }
        });
        this.handleOnCorrectShapeSelection();
      } else {
        this.setHiddenMatManActivityDescription('Incorrect selection, try again.');
        setTimeout(() => {
          this.setHiddenMatManActivityDescription("Mat Man's torso.");
        }, 2000);

        this.sndError();
        this.logError(shapeSelection, currentRoundShape, 'DNDplace');
      }
    },

    handleOnCorrectShapeSelection() {
      const self = this;

      this.setHiddenMatManActivityDescription(
        `Mat Man's torso is in the shape of a ${this.currentRound.shape.toLowerCase()}.`
      );

      TweenMax.to('.drag-panel', 0.2, {
        opacity: 0,
        onComplete() {
          self.showImage = true;
          self.$nextTick(() =>
            TweenMax.to('.drag-completed', 0.3, {
              opacity: 1,
              onComplete() {
                TweenMax.delayedCall(4, () => {
                  if (self.isLastRound) {
                    self.completeActivity();
                    return;
                  }
                  TweenMax.to('.drag-completed', 1, {
                    opacity: 0,
                    onComplete() {
                      self.showImage = false;
                      self.$nextTick(() => {
                        self.setupNewRound();
                        self.setHiddenMatManActivityDescription("Mat Man's torso.");
                        self.$nextTick(() => self.handleRoundStart(true));
                      });
                    }
                  });
                });
              }
            })
          );
        }
      });
    },

    setHiddenMatManActivityDescription(description) {
      const hiddenMatManActivityDescriptionEl = document.getElementById(
        'hidden-mat-man-activity-description'
      );
      if (hiddenMatManActivityDescriptionEl)
        hiddenMatManActivityDescriptionEl.innerText = description;
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/fade.scss';

#dragging-area {
  display: block;
  margin: 0 auto;
  height: auto;
  width: 300px;
}

#hidden-mat-man-activity-description {
  height: 1px;
  opacity: 0;
  position: absolute;
  top: 45px;
  width: 1px;
}

.image-header {
  font-size: 35px;
  font-family: hwtfutura-bold;
  text-transform: uppercase;
  text-align: center;
  display: block;
}

.drag-panel {
  opacity: 0;
}

.drag-completed {
  opacity: 0;
  width: 100%;
  height: 100%;
  box-sizing: border-box;

  .completed-image {
    display: block;
    height: calc(100% - 51px);
    margin: 0 auto;
    min-height: 380px;
    width: auto;
  }
}

.mat-man-match {
  height: 100%;
  box-sizing: border-box;
}

.draggable-items {
  box-sizing: border-box;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-evenly;
  width: 100%;

  .draggable-item {
    align-items: center;
    display: flex;
    flex-direction: column;
    height: auto;
    justify-content: flex-end;
  }
}

.draggable-items.isMobile {
  padding: 0px 15px !important;
}

.selected-image,
.unselected-image {
  left: 50%;
  transform: translateX(-50%);
}

.image-container {
  position: relative;

  .selected-image {
    position: relative;
    z-index: 150;
  }

  .unselected-image {
    position: absolute;
    z-index: 100;
  }
}

.image-text {
  margin-top: 2px;
  font-family: hwtfutura-bold;
  text-transform: uppercase;
  text-align: center;
  font-weight: bold;
  font-size: 16px;
}

.chrome-os,
.tablet {
  .selected-image,
  .unselected-image {
    left: 50%;
    transform: translateX(-50%) scale(0.75);
  }

  #dragging-area {
    width: 225px;
  }
}
</style>
